import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ROUTES } from "../../utils/constants";

export interface AuthSplitLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  showBackButton?: boolean;
}

const BG_SRC = "/images/tkm_land_section5.jpeg";

export const AuthSplitLayout: React.FC<AuthSplitLayoutProps> = ({
  children,
  title,
  subtitle,
  showBackButton = true,
}) => {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen overflow-hidden bg-black">
      {/* ✅ FULL PAGE BG — FULL COVER + CENTER */}
      <img
        src={BG_SRC}
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute inset-0 h-full w-full object-cover object-center blur-2xl opacity-65"
      />
      <img
        src={BG_SRC}
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute inset-0 h-full w-full object-cover object-center"
      />

      {/* ✅ TOP STRIP (RIGHT SIDE ONLY) — THICKER (covers from middle to right edge) */}
      <div className="hidden lg:block absolute top-0 left-1/2 right-0 z-20">
        <div className="relative w-full bg-black/25 backdrop-blur-xl shadow-[0_14px_34px_rgba(0,0,0,0.38)]">
          {/* subtle inner highlight */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/18 via-transparent to-transparent" />

          <div className="relative flex items-center h-20 px-7">
            {/* Center Tamil text */}
            <div className="w-full text-center">
              <span className="text-white font-extrabold tracking-wide text-xl drop-shadow-[0_10px_22px_rgba(0,0,0,0.7)]">
                தமிழ் கற்றல் மையம்
              </span>
            </div>

            {/* Right TKM pill */}
            <div className="absolute right-7">
              <span className="text-white/95 font-black tracking-wider text-base px-5 py-2 rounded-full bg-white/10 ring-1 ring-white/20 backdrop-blur-md drop-shadow-[0_10px_22px_rgba(0,0,0,0.6)]">
                TKM
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left: form panel */}
        <div className="flex flex-col min-h-screen lg:min-h-0 overflow-y-auto bg-app-surface/90 dark:bg-gray-900/85 backdrop-blur-xl">
          <div className="flex flex-1 flex-col items-center justify-center p-6 sm:p-8 lg:p-10">
            <div className="w-full max-w-md space-y-8">
              {showBackButton && (
                <button
                  type="button"
                  onClick={() => navigate(ROUTES.LANDING)}
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors -ml-1"
                  aria-label="Back to home"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Back
                </button>
              )}

              {(title || subtitle) && (
                <div>
                  {title && (
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {title}
                    </h1>
                  )}
                  {subtitle && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      {subtitle}
                    </p>
                  )}
                </div>
              )}

              {children}
            </div>
          </div>
        </div>

        {/* Right column empty (bg already covers full page) */}
        <div className="hidden lg:block" aria-hidden="true" />
      </div>
    </div>
  );
};
